import React from 'react';
import { SignableOfferForm } from '..';
import { OfferDetails } from './OfferDetails';
import { HorizontalStepper } from '../../../../../components/HorizontalStepper';
import { OfferEmail } from './OfferEmail';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { OfferSummary } from './OfferSummary';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { OfferTemplate } from './OfferTemplate';
import { SignableOfferTemplate } from '../../../../../entities/v1/applicant_tracking/SignableOfferTemplate';
import { Signatory } from '../../../../../entities/v1/applicant_tracking/Signatory';
import { OfferDocuments } from '../OfferDocuments';
import { getInitialForm } from './signableOffersInitialForm';

interface PropTypes {
  applicationId: number;
  elementIdentifier: string;
  files: File[];
  form: SignableOfferForm;
  isLoading: boolean;
  offerRequisitionFlowEnabled: boolean;
  step: number;
  to: string;
  variables: string[];
  editMode: boolean;
  clearUploadedFiles: () => void;
  removeAttachmentFileByIndex: (index: number) => void;
  setForm: (form: SignableOfferForm) => void;
  setValidStep: (valid: boolean) => void;
  setVariables: (variables: string[]) => void;
}

const STEPPER_ITEMS = [
  {
    title: 'Offer Template',
    subtitle: 'Select Offer Template',
  },
  {
    title: 'Offer Details',
    subtitle: 'Add Offer Details',
  },
  {
    title: 'Offer Email',
    subtitle: 'Review Offer Email',
  },
];

function BodySelector(
  props: PropTypes & {
    variables: string[];
    setVariables: (variables: string[]) => void;
  },
) {
  if (props.isLoading) {
    return <LoadingSpinner />;
  }

  switch (props.step) {
    case 0:
      return (
        <OfferTemplate
          setIsValid={props.setValidStep}
          offerTemplate={props.form.signableOfferTemplate}
          signatory={props.form.signatory}
          onChangeTemplate={(
            offerTemplate: SignableOfferTemplate,
            signatory?: Signatory,
          ) => {
            props.setVariables(null);
            props.setForm({
              ...getInitialForm(),
              to: props.to,
              signableOfferTemplate: offerTemplate,
              signatory: signatory,
            });
          }}
          onChangeSignatory={(signatory?: Signatory) =>
            props.setForm({ ...props.form, signatory: signatory })
          }
        />
      );
    case 1:
      return (
        <OfferDetails
          applicationId={props.applicationId}
          form={props.form}
          offerTemplate={props.form.signableOfferTemplate}
          variables={props.variables}
          setForm={props.setForm}
          setIsValid={props.setValidStep}
          setVariables={props.setVariables}
        />
      );
    case 2:
      return (
        <OfferEmail
          applicationIds={[props.applicationId]}
          form={props.form}
          files={props.files}
          editMode={props.editMode}
          clearUploadedFiles={props.clearUploadedFiles}
          removeAttachmentFileByIndex={props.removeAttachmentFileByIndex}
          setForm={props.setForm}
          setIsValid={props.setValidStep}
        />
      );
  }
}

export function BodyWrapper(props: PropTypes) {
  return props.step < STEPPER_ITEMS.length ? (
    <>
      <HorizontalStepper
        displayInModal
        items={STEPPER_ITEMS}
        activeIndex={props.step}
      />
      <div
        id={props.elementIdentifier}
        className={classNames(
          styles['modal-body'],
          'px-4',
          'mb-3',
          'overflow-auto',
        )}
      >
        <BodySelector
          {...props}
          variables={props.variables}
          setVariables={props.setVariables}
        />
      </div>
    </>
  ) : (
    <OfferSummary
      applicationId={props.applicationId}
      className={styles['modal-body']}
      form={props.form}
      signatoryName={props.form.signatory?.user?.name}
      isLoading={props.isLoading}
      emailHtml={props.form.email}
      emailAttachments={props.form.attachments}
      signableDocumentsNode={
        <OfferDocuments
          signableDocuments={props.form.signableOfferTemplate.signableDocuments}
          form={props.form}
          applicationId={props.applicationId}
        />
      }
      offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
    />
  );
}
